import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X as CloseIcon, ShieldAlert, Check } from 'lucide-react';

import { Avatar } from './Avatar';
import { Btn } from './Btn';
import { useToast } from './Toast';
import {
  REPORT_REASONS, reportUser, blockUser,
  SafetyError, type ReportReason, type ReportContext,
} from '../../services/safetyService';

/**
 * "This person, and never again."
 *
 * Report and block are ONE dialog because in the moment they are one intention.
 * A parent watching a stranger type something vile at their kid does not want a
 * moderation workflow; they want that person gone, now, and someone told. Making
 * those two separate journeys — a report here, a block buried in settings —
 * means most people do one and believe they did both.
 *
 * So: pick a reason, and "also block them" is ON by default. The block is the
 * part that protects you (it is enforced at the door, symmetrically, on every
 * join and every Quick Match); the report is the part that protects everyone
 * else. Unticking is allowed — reporting someone for cheating is not the same
 * as never wanting to see them again — but the safe thing is the default thing.
 *
 * The note is optional. Requiring an explanation is how you get zero reports.
 */
export interface ReportDialogProps {
  uid: string;
  name: string;
  avatar?: string | null;
  /** Where this happened, so a human reading the queue has somewhere to look. */
  contextType: ReportContext;
  contextId?: string | null;
  onClose: () => void;
  /** Fired after a successful block, so the surface behind can drop the player. */
  onBlocked?: (uid: string) => void;
}

export function ReportDialog({
  uid, name, avatar, contextType, contextId, onClose, onBlocked,
}: ReportDialogProps) {
  const toast = useToast();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason || busy) return;
    setBusy(true);
    try {
      await reportUser({ targetUid: uid, reason, contextType, contextId, note });

      // The report is filed. If the block fails we still succeeded at the thing
      // that is hardest to redo, so it is reported separately rather than
      // rolling the whole gesture back.
      if (alsoBlock) {
        try {
          await blockUser(uid);
          onBlocked?.(uid);
          toast.success(`Thanks. We've got it, and ${name} can't join your games.`);
        } catch {
          toast.info(`Report sent. We couldn't block ${name} — you can try from Settings.`);
        }
      } else {
        toast.success("Thanks. We've got it — a real person reads these.");
      }
      onClose();
    } catch (e) {
      toast.error(e instanceof SafetyError ? e.userMessage : "That didn't send. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={`Report ${name}`}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 240, // above ProfilePreview (220) — it opens from there
          background: 'rgba(0,0,0,0.42)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <motion.div
          key="card"
          initial={{ y: 16, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 8, opacity: 0, scale: 0.97 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          onClick={e => e.stopPropagation()}
          className="card lg"
          style={{
            width: '100%',
            maxWidth: 380,
            background: 'var(--surface)',
            padding: 20,
            position: 'relative',
            maxHeight: '85vh',
            overflowY: 'auto',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute', top: 10, right: 10,
              width: 28, height: 28, borderRadius: 8,
              border: 'none', background: 'rgba(0,0,0,0.04)',
              cursor: 'pointer', color: 'var(--ink-2)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <CloseIcon size={14} />
          </button>

          <div className="wr-row gap-3" style={{ alignItems: 'center', marginBottom: 4 }}>
            <Avatar id={avatar} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="h3" style={{ fontSize: 16 }}>Report {name}</div>
              <div className="wr-text-3" style={{ fontSize: 12 }}>What happened?</div>
            </div>
          </div>

          {/* The reasons. Radio semantics, big tap targets — a kid on a phone
              is a real user of this list. */}
          <div
            role="radiogroup"
            aria-label="Reason"
            className="wr-col gap-2"
            style={{ marginTop: 14 }}
          >
            {REPORT_REASONS.map(r => {
              const active = reason === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setReason(r.value)}
                  className="card wr-between"
                  style={{
                    width: '100%',
                    minHeight: 44,
                    padding: '11px 14px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    alignItems: 'center',
                    background: active ? 'var(--accent-soft)' : 'var(--surface)',
                    boxShadow: active
                      ? '0 0 0 1.5px var(--accent)'
                      : '0 0 0 1px var(--line)',
                  }}
                >
                  <span style={{ fontSize: 14, color: 'var(--ink-1)' }}>{r.label}</span>
                  {active && <Check size={15} color="var(--accent)" strokeWidth={3} />}
                </button>
              );
            })}
          </div>

          <textarea
            className="field text"
            value={note}
            onChange={e => setNote(e.target.value.slice(0, 500))}
            placeholder="Anything else? (optional)"
            rows={2}
            aria-label="Extra detail, optional"
            style={{
              width: '100%', marginTop: 12, resize: 'vertical',
              fontFamily: 'var(--ui)', fontSize: 14,
            }}
          />

          <button
            type="button"
            role="checkbox"
            aria-checked={alsoBlock}
            onClick={() => setAlsoBlock(v => !v)}
            className="card wr-between"
            style={{
              width: '100%', marginTop: 12, padding: '11px 14px',
              minHeight: 44, cursor: 'pointer', alignItems: 'center',
              textAlign: 'left', background: 'var(--surface)',
            }}
          >
            <span style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
              <span style={{ fontSize: 14, color: 'var(--ink-1)', display: 'block' }}>
                Also block {name}
              </span>
              <span className="wr-text-3" style={{ fontSize: 12, lineHeight: 1.35, display: 'block', marginTop: 1 }}>
                They won't be able to join your games, and you'll never be matched together.
              </span>
            </span>
            <span className={`wr-toggle${alsoBlock ? ' on' : ''}`} aria-hidden />
          </button>

          <div className="wr-col gap-2" style={{ marginTop: 16 }}>
            <Btn
              kind="primary"
              full
              icon="ShieldAlert"
              disabled={!reason}
              loading={busy}
              onClick={submit}
            >
              Send report
            </Btn>
            <Btn kind="ghost" full onClick={onClose} disabled={busy}>Cancel</Btn>
          </div>

          <p
            className="wr-text-3"
            style={{ fontSize: 11, lineHeight: 1.45, marginTop: 12, textAlign: 'center' }}
          >
            <ShieldAlert size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
            A real person reads every report. {name} is never told who sent it.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ReportDialog;
